import { useEffect, useState } from 'react';
import { Brain, ChevronDown, ChevronRight } from 'lucide-react';
import type { ChatMessage as ChatMessageType } from '../../types';
import { buildMemoryClusterSummary } from '../../utils/memory-activity-summary';
import { ACTIVITY_SETTLE_DELAY_MS, useDelayedSettle } from '@/hooks/use-delayed-settle';

interface MemoryActivityClusterProps {
  /** Consecutive memory delta messages for one run + subType, in order. */
  messages: ChatMessageType[];
  /** True while aside text is still streaming. */
  live?: boolean;
}

export const MemoryActivityCluster = ({ messages, live = false }: MemoryActivityClusterProps) => {
  // Collapses on its own timeline once all asides finalize, independent of turn end.
  const finished = messages.length > 0 && messages.every((m) => m.finalizedAt);
  const complete = useDelayedSettle(finished, ACTIVITY_SETTLE_DELAY_MS);
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);

  useEffect(() => {
    if (live) setUserExpanded(null);
  }, [live]);

  if (!messages.length) return null;

  const subType = messages[0]?.memorySubType;
  const count = messages.reduce(
    (max, m) => Math.max(max, m.memoryCount ?? 1),
    1,
  );
  const summary = buildMemoryClusterSummary(subType, count);
  const collapsed = userExpanded === null ? complete : !userExpanded;

  return (
    <div>
      {complete && (
        <button
          type="button"
          className="agent-block-enter flex w-full items-center gap-2 text-left text-[11px] text-muted-foreground/75 transition-opacity hover:opacity-80"
          onClick={() => setUserExpanded(collapsed)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Show memory activity details' : 'Collapse memory activity details'}
        >
          {collapsed ? (
            <ChevronRight className="h-3 w-3 shrink-0" />
          ) : (
            <ChevronDown className="h-3 w-3 shrink-0" />
          )}
          <span className="truncate">{summary}</span>
        </button>
      )}
      <div className="agent-activity-collapse" data-open={!collapsed}>
        <div className={`min-h-0 overflow-hidden ${complete ? 'pt-0.5' : ''}`}>
          <div className="flex flex-col" role="list" aria-label="Memory activity">
            {messages.map((message, idx) => {
              const isLast = idx === messages.length - 1;

              return (
                <div
                  key={message.id}
                  className="agent-block-enter flex items-start gap-2"
                  role="listitem"
                >
                  <Brain
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/70"
                    aria-hidden
                  />
                  <div className={`flex-1 min-w-0 ${isLast ? '' : 'pb-1.5'}`}>
                    <p className="text-[11px] leading-relaxed text-muted-foreground/75">
                      {message.content}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
